// Payment reconciliation for checkout.
//
// Razorpay's `handler` (success) and `ondismiss` callbacks are unreliable in a
// mobile WebView + UPI-intent flow: the user leaves to pay in GPay/PhonePe, the
// money is captured, and they return — but the callback frequently never fires,
// leaving them stranded on the checkout screen even though the backend webhook
// (`payment.captured`) has already provisioned the subscription.
//
// This module makes the order status endpoint the source of truth. After the
// gateway opens we poll `GET /api/v1/orders/user/order-status/<id>/` until the
// order is `paid` (flipped by either the verify handler OR the webhook, both
// idempotent) or a timeout elapses. The pending order is mirrored to
// localStorage so a reload / app-kill mid-payment resumes reconciliation on the
// next mount.

import axios from './axios';

const PENDING_PAYMENT_KEY = 'pendingPayment';
// Hard TTL: after this we stop auto-resuming a pending payment on mount. The
// webhook still provisions server-side, so the user sees it on their dashboard.
const PENDING_TTL_MS = 20 * 60 * 1000; // 20 minutes

// The meeting room checkout keeps its own record so each checkout page only
// ever recovers its own orders.
export const MEETING_ROOM_PAYMENT = 'pendingMeetingRoomPayment';

export const savePendingPayment = (ctx, key = PENDING_PAYMENT_KEY) => {
  try {
    localStorage.setItem(key, JSON.stringify({ ...ctx, createdAt: Date.now() }));
  } catch (e) {
    // storage full / disabled — reconciliation still works in-session
  }
};

export const getPendingPayment = (key = PENDING_PAYMENT_KEY) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.orderId) return null;
    if (Date.now() - (parsed.createdAt || 0) > PENDING_TTL_MS) {
      clearPendingPayment(key);
      return null;
    }
    return parsed;
  } catch (e) {
    return null;
  }
};

export const clearPendingPayment = (key = PENDING_PAYMENT_KEY) => {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    /* no-op */
  }
};

// How long after opening the gateway an app restart still sends the member
// back to the checkout to see the payment confirmed.
const RESUME_WINDOW_MS = 15 * 60 * 1000;

// Which page confirms each kind of pending payment.
const RESUME_PAGES = [
  { key: PENDING_PAYMENT_KEY, path: '/user/checkout' },
  { key: MEETING_ROOM_PAYMENT, path: '/user/meeting-room/checkout' },
];

/**
 * The checkout page to send the member back to, handed out once per payment.
 *
 * The app can drop the member on the home page while they are paying in GPay,
 * and only the checkout page knows how to confirm the payment, so they never
 * see it land. The caller sends them back there. Each payment is marked as
 * offered so a member who then walks away is not dragged back again.
 */
export const takeResumablePayment = () => {
  const found = RESUME_PAGES.find(({ key }) => {
    const pending = getPendingPayment(key);
    return pending && !pending.resumeOffered && Date.now() - (pending.createdAt || 0) <= RESUME_WINDOW_MS;
  });
  if (!found) return null;
  try {
    const pending = getPendingPayment(found.key);
    localStorage.setItem(found.key, JSON.stringify({ ...pending, resumeOffered: true }));
  } catch (e) {
    // Could not mark it, so do not redirect: a redirect we cannot stop repeating
    // is worse than the member checking My Orders themselves.
    return null;
  }
  return found.path;
};

/**
 * Fetch the current settlement state of an order.
 * @returns {Promise<{paid:boolean, status:string, is_active:boolean, invoice_id:?string, invoice_number:?string}>}
 */
export const fetchOrderStatus = async (orderId) => {
  const { data } = await axios.get(`/api/v1/orders/user/order-status/${orderId}/`);
  return data;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Poll the order status until it is paid or the timeout elapses.
 *
 * Resolves — never rejects — so callers can branch on `outcome`:
 *   { outcome: 'paid',    data }  → order settled, provision confirmed
 *   { outcome: 'timeout', data }  → not confirmed within window (may still land later)
 *   { outcome: 'cancelled' }      → caller aborted via isCancelled()
 *
 * Transient network errors are swallowed and retried (mobile flakiness).
 *
 * @param {string} orderId
 * @param {object} opts
 * @param {number} [opts.intervalMs=3000]
 * @param {number} [opts.timeoutMs=180000]   default 3 min
 * @param {() => boolean} [opts.isCancelled]  return true to stop early
 * @param {(status:object) => void} [opts.onTick]  called with each successful poll result
 */
export const pollOrderStatus = async (
  orderId,
  { intervalMs = 3000, timeoutMs = 180000, isCancelled, onTick } = {}
) => {
  const deadline = Date.now() + timeoutMs;
  let lastData = null;

  // Immediate first check so an already-settled payment resolves without waiting.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (isCancelled && isCancelled()) return { outcome: 'cancelled', data: lastData };

    try {
      // eslint-disable-next-line no-await-in-loop
      const data = await fetchOrderStatus(orderId);
      lastData = data;
      if (onTick) onTick(data);
      if (data?.paid) return { outcome: 'paid', data };
      // A genuinely cancelled order (e.g. admin cancels) is terminal too.
      if (data?.status === 'cancelled') return { outcome: 'cancelled', data };
    } catch (e) {
      // 404 for the current user means it's not their order / gone — stop.
      if (e?.response?.status === 404) return { outcome: 'timeout', data: lastData };
      // otherwise transient — keep polling
    }

    if (Date.now() >= deadline) return { outcome: 'timeout', data: lastData };
    // eslint-disable-next-line no-await-in-loop
    await sleep(intervalMs);
  }
};
